/**
 * API Client with Clerk Authentication Interceptors
 *
 * Features:
 * - Automatic Clerk token attachment
 * - Request/response interceptors
 * - Error handling
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.codicecivile.ai/api/v1';

// Token getter function that will be set by the app
let clerkTokenGetter: (() => Promise<string | null>) | null = null;

/**
 * Set the Clerk token getter function (should be called from Clerk's useAuth)
 */
export function setClerkTokenGetter(getter: () => Promise<string | null>) {
  clerkTokenGetter = getter;
}

/**
 * Get the current Clerk token getter function
 * Used by services that need direct access to tokens (e.g., streaming endpoints)
 */
export function getClerkTokenGetter(): (() => Promise<string | null>) | null {
  return clerkTokenGetter;
}

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies for Clerk session
});

// Request interceptor - attach Clerk token
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      // Get token from Clerk
      if (clerkTokenGetter) {
        const token = await clerkTokenGetter();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    } catch (error) {
      console.error('Failed to get Clerk token:', error);
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // If 401, let Clerk handle the redirect to login
    if (error.response?.status === 401) {
      console.error('Authentication failed - user may need to sign in again');
    }
    return Promise.reject(error);
  }
);

// API Error type
export interface APIError {
  message: string;
  detail?: string;
  statusCode?: number;
}

// Error handler utility
export const handleAPIError = (error: unknown): APIError => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ detail?: string | Array<{msg: string; type: string}>; message?: string }>;
    let errorMessage: string;
    let errorDetail: string | undefined;

    // Handle Pydantic validation errors (detail is an array)
    if (Array.isArray(axiosError.response?.data?.detail)) {
      const validationErrors = axiosError.response.data.detail;
      errorMessage = validationErrors.map(err => err.msg || err.type).join(', ');
      errorDetail = JSON.stringify(validationErrors);
    } else {
      // Handle simple string detail or message
      errorMessage = axiosError.response?.data?.detail as string || axiosError.response?.data?.message || axiosError.message;
      errorDetail = axiosError.response?.data?.detail as string;
    }

    return {
      message: errorMessage,
      detail: errorDetail,
      statusCode: axiosError.response?.status,
    };
  }
  return {
    message: error instanceof Error ? error.message : 'An unknown error occurred',
  };
};

export default api;
